// client/src/components/company/NewRequest.js
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { sanitizeInput } from '../utils/sanitize';
import { parseCurrencyInput, handleCurrencyInputChange, availableCurrencies, getCurrencySymbol } from '../utils/formatUtils';
import { PlusCircle, Building2, Globe, Info } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const NewRequest = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { type } = useParams();
  const isHotMoney = type === 'hot-money';

  // Recupera isPaying dall'organizzazione dell'utente
  // Se isPaying non esiste, considera come true
  const isPaying = user?.organization?.isPaying !== false;
  
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'EUR',
    rateTypes: ['fixed', 'floating'],
    duration: '',
    customStartDate: '',
    customEndDate: '',
    withdrawalType: '',
    bidDeadline: '',
    bidDeadlineTime: '', // Campo orario opzionale (default 23:59 se vuoto)
    notes: '',
    targetBanks: isPaying ? 'custom' : 'third_party',
    selectedBanks: [] // Array di userIds
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [affiliatedBanks, setAffiliatedBanks] = useState([]);
  const [thirdPartyBanks, setThirdPartyBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  // renewalFlags: { [organizationId]: boolean }
  const [renewalFlags, setRenewalFlags] = useState({});
  const navigate = useNavigate();
  
  const { amount, currency, rateTypes, duration, customStartDate, customEndDate, withdrawalType, bidDeadline, bidDeadlineTime, notes, targetBanks, selectedBanks } = formData;
  
  // Helper function to get bank display name (must be before useMemo)
  const getBankDisplayName = (bank) => {
    if (bank.referralDetails?.bankName) {
      return bank.referralDetails.bankName;
    }
    if (bank.bankName) {
      return bank.bankName;
    }
    return `${bank.firstName || ''} ${bank.lastName || ''}`.trim();
  };
  
  // ✅ NUOVO: Raggruppa le banche per bankName
  const groupedBanks = useMemo(() => {
    const groups = {};
    
    affiliatedBanks.forEach(bank => {
      const bankName = getBankDisplayName(bank);
      
      if (!groups[bankName]) {
        groups[bankName] = {
          bankName,
          users: [],
          organizationId: bank.organizationId
        };
      }
      
      groups[bankName].users.push({
        userId: bank._id || bank.userId,
        firstName: bank.firstName,
        lastName: bank.lastName,
        email: bank.email
      });
    });
    
    return Object.values(groups);
  }, [affiliatedBanks]);

  const groupedThirdPartyBanks = useMemo(() => {
  const groups = {};
  
  thirdPartyBanks.forEach(bank => {
    const orgName = bank.organization?.name || 'Unknown Bank';
    const orgId = bank.organization?._id || bank._id;
    
    if (!groups[orgId]) {
      groups[orgId] = {
        bankName: orgName,
        organizationId: orgId,
        users: []
      };
    }
    
    groups[orgId].users.push({
      userId: bank._id,
      firstName: bank.firstName,
      lastName: bank.lastName,
      email: bank.email
    });
  });
  
  return Object.values(groups);
}, [thirdPartyBanks]);
    
  useEffect(() => {
    const fetchAffiliatedBanks = async () => {
      try {
        setLoadingBanks(true);

        const response = await axios.get('/api/company/bank-referrals');
        setAffiliatedBanks(response.data.bankReferrals || []);

        if (isPaying) {
          const tpResponse = await axios.get('/api/requests/third-party-banks');
          setThirdPartyBanks((tpResponse.data.thirdPartyBanks || []).filter(bank =>
            bank.organization?.name !== 'Banca Demo EO' &&
            bank.organization?.name !== 'Allianz Bank Financial Advisors'
          ));
        }
      } catch (err) {
        console.error('Error fetching banks:', err);
        setAffiliatedBanks([]);
        setThirdPartyBanks([]);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchAffiliatedBanks();
  }, [isPaying]);

useEffect(() => {
  if (!isPaying) {
    setFormData(prev => ({
      ...prev,
      targetBanks: 'third_party',
      selectedBanks: []
    }));
  }
}, [isPaying]);

const toggleThirdPartyBankSelection = (bankGroup) => {
  const userIds = bankGroup.users.map(u => u.userId);
  const allSelected = userIds.every(id => selectedBanks.includes(id));
  
  let updatedBanks;
  if (allSelected) {
    updatedBanks = selectedBanks.filter(id => !userIds.includes(id));
  } else {
    updatedBanks = [...new Set([...selectedBanks, ...userIds])];
  }
  
  setFormData({ ...formData, selectedBanks: updatedBanks });
};

const isThirdPartyBankSelected = (bankGroup) => {
  const userIds = bankGroup.users.map(u => u.userId);
  return userIds.every(id => selectedBanks.includes(id));
};
  
  const onChange = e => {
    if (e.target.name === 'amount') {
      handleCurrencyInputChange(e, setFormData, formData, 'amount');
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const onRateTypeChange = (rateType) => {
    const updatedRateTypes = rateTypes.includes(rateType)
      ? rateTypes.filter(type => type !== rateType)
      : [...rateTypes, rateType];
    
    setFormData({ ...formData, rateTypes: updatedRateTypes });
  };
  
  // ✅ MODIFICATO: Toggle per gruppo di banca (tutti gli utenti insieme)
  const toggleBankGroupSelection = (bankGroup) => {
    
    const userIds = bankGroup.users.map(u => u.userId);
    
    // Controlla se TUTTI gli utenti di questa banca sono già selezionati
    const allSelected = userIds.every(id => selectedBanks.includes(id));
    
    let updatedBanks;
    if (allSelected) {
      // Rimuovi TUTTI gli utenti di questa banca
      updatedBanks = selectedBanks.filter(id => !userIds.includes(id));
    } else {
      // Aggiungi TUTTI gli utenti di questa banca
      updatedBanks = [...new Set([...selectedBanks, ...userIds])];
    }
    
    setFormData({ ...formData, selectedBanks: updatedBanks });
  };
  
  // ✅ NUOVO: Controlla se un gruppo è selezionato
  const isBankGroupSelected = (bankGroup) => {
    const userIds = bankGroup.users.map(u => u.userId);
    return userIds.every(id => selectedBanks.includes(id));
  };
  
  const toggleRenewalFlag = (organizationId) => {
    setRenewalFlags(prev => ({
      ...prev,
      [organizationId]: !prev[organizationId]
    }));
  };

  const onDurationChange = e => {
    const newDuration = e.target.value;
    let newWithdrawalType = withdrawalType;

    if (newDuration === 'overnight') {
      newWithdrawalType = 'demand';
    }

    setFormData({
      ...formData,
      duration: newDuration,
      withdrawalType: newWithdrawalType,
      // Clear custom dates when switching away from custom
      customStartDate: newDuration === 'custom' ? formData.customStartDate : '',
      customEndDate: newDuration === 'custom' ? formData.customEndDate : ''
    });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const formattedAmount = parseCurrencyInput(amount);
    
    if (isNaN(formattedAmount) || formattedAmount <= 0) {
      setError(t('forms.validation.validAmount'));
      setLoading(false);
      return;
    }
    
    if (rateTypes.length === 0) {
      setError(t('forms.validation.selectRateType'));
      setLoading(false);
      return;
    }
    
    // Validate custom dates
    if (duration === 'custom') {
      if (!customStartDate || !customEndDate) {
        setError(t('requests.customDatesRequired'));
        setLoading(false);
        return;
      }
      if (new Date(customStartDate) < new Date(formatDate())) {
        setError(t('requests.customStartFuture'));
        setLoading(false);
        return;
      }
      if (new Date(customEndDate) <= new Date(customStartDate)) {
        setError(t('requests.customEndAfterStart'));
        setLoading(false);
        return;
      }
    }

    if (!isHotMoney && !withdrawalType) {
      setError(t('forms.validation.validWithdrawal'));
      setLoading(false);
      return;
    }
    
    if (affiliatedBanks.length > 0 && targetBanks === 'affiliated' && selectedBanks.length === 0) {
      setError(t('requests.selectAtLeastOneBank') || 'Please select at least one bank');
      setLoading(false);
      return;
    }
    
    try {
      // Build bankRenewalFlags from renewalFlags state (only relationship banks marked as renewal)
      const bankRenewalFlagsPayload = isPaying
        ? groupedBanks
            .filter(bg => bg.organizationId && renewalFlags[bg.organizationId])
            .map(bg => ({ bankOrganization: bg.organizationId, isRenewal: true }))
        : [];

      const requestData = {
        amount: formattedAmount,
        currency,
        rateTypes,
        duration,
        ...(duration === 'custom' ? { customStartDate, customEndDate } : {}),
        withdrawalType: isHotMoney ? 'demand' : withdrawalType,
        bidDeadline,
        bidDeadlineTime: bidDeadlineTime || '',
        notes: sanitizeInput(notes),
        targetBanks: isPaying ? 'custom' : 'third_party',
        selectedBanks: isPaying ? selectedBanks : [],
        bankRenewalFlags: isHotMoney ? [] : bankRenewalFlagsPayload,
        requestType: isHotMoney ? 'hot_money' : 'allocation'
      };

      await axios.post('/api/requests', requestData);
      navigate(`/company/past-requests/${isHotMoney ? 'hot-money' : 'allocation'}`);
    } catch (err) {
      setError(err.response?.data?.message || t('common.error.createRequest', { 
        defaultValue: 'Failed to create request' 
      }));
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    let month = today.getMonth() + 1;
    let day = today.getDate();
    
    month = month < 10 ? `0${month}` : month;
    day = day < 10 ? `0${day}` : day;
    
    return `${year}-${month}-${day}`;
  };

  const getWithdrawalOptions = () => {
    if (duration === 'overnight') {
      return [
        { value: 'demand', label: t('requests.demand') }
      ];
    }
    return [
      { value: 'early_allowed', label: t('requests.earlyWithdrawalAllowed') },
      { value: 'no_withdrawal', label: t('requests.noWithdrawalAllowed') }
    ];
  };
  
  return (
    <div className="company-dashboard">
      <div className="card">
        <div className="card-title">
          <PlusCircle className="card-title-icon" size={20} />
          <span>{isHotMoney ? t('requests.newHotMoneyRequest') : t('requests.newLiquidityRequest')}</span>
        </div>
        
        {error && <div className="alert alert-danger">{error}</div>}
        
        <form id="new-request-form" onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor="currency">
              {t('requests.currency') || 'Currency'} *
            </label>
            <select
              id="currency"
              name="currency"
              className="form-select-custom"
              value={currency}
              onChange={onChange}
              required
              disabled={!isPaying}
              style={!isPaying ? { backgroundColor: '#e9ecef', color: '#6c757d', cursor: 'not-allowed' } : {}}
            >
              {availableCurrencies
                .filter(curr => isPaying || curr.code === 'EUR')
                .map(curr => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code}
                  </option>
                ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="amount">
              {t('requests.totalAmount')} ({getCurrencySymbol(currency)}) *
            </label>
            <input
              type="text"
              id="amount"
              name="amount"
              placeholder={t('forms.enterAmount')}
              value={amount}
              onChange={onChange}
              required
            />
          </div>
          
          {/* Rate type selection - only for paying customers, non-paying default to both fixed and floating */}
          {isPaying && (
            <div className="form-group">
              <label>
                {t('requests.acceptedRateTypesNR')} *
              </label>
              <div className="checkbox-group">
                <div className="checkbox-label">
                  <input
                    type="checkbox"
                    id="rateType-fixed"
                    checked={rateTypes.includes('fixed')}
                    onChange={() => onRateTypeChange('fixed')}
                  />
                  <span>{t('requests.fixed')}</span>
                </div>
                <div className="checkbox-label">
                  <input
                    type="checkbox"
                    id="rateType-floating"
                    checked={rateTypes.includes('floating')}
                    onChange={() => onRateTypeChange('floating')}
                  />
                  <span>{t('requests.floating')}</span>
                </div>
              </div>
              <small className="form-help-text">
                {t('requests.rateTypesHelp')}
              </small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="duration">
              {t('requests.maturity')} *
            </label>
            <select
              id="duration"
              name="duration"
              className="form-select-custom"
              value={duration}
              onChange={onDurationChange}
              required
            >
              <option value="">{t('requests.selectMaturity')}</option>
              {isPaying && <option value="custom">{t('requests.custom')}</option>}
              <option value="overnight">{t('requests.overnight')}</option>
              {/* ✅ CONDIZIONA: 7d, 14d, 21d solo per paganti */}
              {isPaying && <option value="7d">7 {t('common.days', { defaultValue: 'days' })}</option>}
              {isPaying && <option value="14d">14 {t('common.days', { defaultValue: 'days' })}</option>}
              {isPaying && <option value="21d">21 {t('common.days', { defaultValue: 'days' })}</option>}
              {/* ✅ CONDIZIONA: 1 e 2 mesi solo per paganti */}
              {isPaying && <option value="1">1 {t('common.month', { defaultValue: 'month' })}</option>}
              {isPaying && <option value="2">2 {t('common.months', { defaultValue: 'months' })}</option>}
              <option value="3">3 {t('common.months', { defaultValue: 'months' })}</option>
              <option value="6">6 {t('common.months', { defaultValue: 'months' })}</option>
              {!isHotMoney && <option value="12">12 {t('common.months', { defaultValue: 'months' })}</option>}
              {!isHotMoney && <option value="18">18 {t('common.months', { defaultValue: 'months' })}</option>}
              {!isHotMoney && <option value="24">24 {t('common.months', { defaultValue: 'months' })}</option>}
            </select>
          </div>

          {/* Custom maturity date inputs - only when custom is selected */}
          {duration === 'custom' && (
            <div className="form-group">
              <label>{t('requests.customDateRange')}</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="customStartDate" style={{ fontSize: '13px', marginBottom: '4px', display: 'block' }}>
                    {t('requests.customStartDate')} *
                  </label>
                  <input
                    type="date"
                    id="customStartDate"
                    name="customStartDate"
                    value={customStartDate}
                    onChange={onChange}
                    min={formatDate()}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="customEndDate" style={{ fontSize: '13px', marginBottom: '4px', display: 'block' }}>
                    {t('requests.customEndDate')} *
                  </label>
                  <input
                    type="date"
                    id="customEndDate"
                    name="customEndDate"
                    value={customEndDate}
                    onChange={onChange}
                    min={customStartDate || formatDate()}
                    required
                  />
                </div>
              </div>
            </div>
          )}
          
          {!isHotMoney && (
            <div className="form-group">
              <label htmlFor="withdrawalType">
                {t('requests.withdrawalOptions')} *
              </label>
              <select
                id="withdrawalType"
                name="withdrawalType"
                className="form-select-custom"
                value={withdrawalType}
                onChange={onChange}
                required
                disabled={duration === 'overnight'}
              >
                <option value="">{t('requests.selectWithdrawalOption')}</option>
                {getWithdrawalOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {duration === 'overnight' && (
                <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                  {t('requests.overnightAutoSet', {
                    defaultValue: 'Overnight deposits are automatically set to "Demand"'
                  })}
                </small>
              )}
            </div>
          )}
          
          {/* Sezione banche: per isPaying, sempre visibile (relationship sempre, third party solo per allocation) */}
          {!loadingBanks && isPaying && (
            <div className="form-group">
              <label>{t('requests.targetBanks') || 'Target Banks'} *</label>

              {/* Relationship Banks — visibili per tutti i tipi */}
              {groupedBanks.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>
                    <Building2 size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    {t('requests.relationshipBanks') || 'Relationship Banks'}
                  </h4>
                  <div className="checkbox-group">
                    {groupedBanks.map((bankGroup, index) => (
                      <div
                        key={`rel-${index}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}
                      >
                        {/* Bank selection checkbox */}
                        <div
                          className="checkbox-label"
                          onClick={() => toggleBankGroupSelection(bankGroup)}
                          style={{ cursor: 'pointer', flex: 1 }}
                        >
                          <input
                            type="checkbox"
                            checked={isBankGroupSelected(bankGroup)}
                            onChange={() => {}}
                            style={{ pointerEvents: 'none' }}
                          />
                          <span style={{ fontWeight: '500' }}>{bankGroup.bankName}</span>
                        </div>
                        {/* Renewal toggle — solo per allocation */}
                        {!isHotMoney && (
                          <div
                            onClick={() => toggleRenewalFlag(bankGroup.organizationId)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: renewalFlags[bankGroup.organizationId]
                                ? '1px solid #a5d6a7'
                                : '1px solid #dee2e6',
                              backgroundColor: renewalFlags[bankGroup.organizationId]
                                ? '#e8f5e9'
                                : '#f8f9fa',
                              flexShrink: 0
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!renewalFlags[bankGroup.organizationId]}
                              onChange={() => {}}
                              style={{ pointerEvents: 'none', width: '12px', height: '12px' }}
                            />
                            <span style={{
                              fontSize: '11px',
                              color: renewalFlags[bankGroup.organizationId] ? '#2e7d32' : '#6c757d',
                              fontWeight: renewalFlags[bankGroup.organizationId] ? '600' : '400',
                              whiteSpace: 'nowrap'
                            }}>
                              {t('requests.renewalFlag')}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Third Party Banks — solo per allocation, non per hot_money */}
              {!isHotMoney && groupedThirdPartyBanks.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>
                    <Globe size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    {t('requests.thirdPartyBanks') || 'Third Party Banks'}
                  </h4>
                  <div style={{
                    marginBottom: '11px',
                    fontSize: '0.82rem',
                    color: '#6c757d',
                    fontStyle: 'italic',
                    lineHeight: '1.3'
                  }}>
                    {t('requests.thirdPartyBanksInfo')}
                  </div>
                  <div className="checkbox-group">
                    {groupedThirdPartyBanks.map((bankGroup, index) => (
                      <div
                        key={`tp-${index}`}
                        className="checkbox-label"
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleThirdPartyBankSelection(bankGroup)}
                      >
                        <input
                          type="checkbox"
                          checked={isThirdPartyBankSelected(bankGroup)}
                          onChange={() => {}}
                          style={{ pointerEvents: 'none' }}
                        />
                        <span style={{ fontWeight: '500' }}>{bankGroup.bankName}</span>
                        <Info
                          size={16}
                          style={{
                            marginLeft: '8px',
                            color: 'var(--primary)',
                            cursor: 'pointer',
                            verticalAlign: 'middle'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/company/bank-info/${encodeURIComponent(bankGroup.bankName)}`);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Counter */}
              <small style={{ color: '#6c757d', marginTop: '12px', display: 'block' }}>
                {groupedBanks.filter(bg => bg.users.some(u => selectedBanks.includes(u.userId))).length +
                 (isHotMoney ? 0 : groupedThirdPartyBanks.filter(bg => bg.users.some(u => selectedBanks.includes(u.userId))).length)} {t('requests.banksSelected') || 'bank(s) selected'}
              </small>
            </div>
          )}

          {loadingBanks && (
            <div className="form-group">
              <small style={{ color: '#6c757d' }}>
                {t('common.loading') || 'Loading affiliated banks...'}
              </small>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="bidDeadline">
              {t('requests.bidSubmissionDeadline')} *
            </label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="date"
                  id="bidDeadline"
                  name="bidDeadline"
                  value={bidDeadline}
                  onChange={onChange}
                  min={formatDate()}
                  required
                />
              </div>
              <div style={{ flex: 1 }}>
                <input
                  type="time"
                  id="bidDeadlineTime"
                  name="bidDeadlineTime"
                  value={bidDeadlineTime}
                  onChange={onChange}
                  placeholder="23:59"
                />
                <small style={{ color: '#6c757d', marginTop: '4px', display: 'block', fontSize: '12px' }}>
                  {t('requests.deadlineTimeHelp', { defaultValue: 'Default: 23:59 if not specified' })}
                </small>
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="notes">
              {t('requests.additionalNotes')}
            </label>
            <textarea
              id="notes"
              name="notes"
              rows="4"
              placeholder={t('forms.additionalRequirements')}
              value={notes}
              onChange={onChange}
            ></textarea>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-light"
              onClick={() => navigate('/company/dashboard')}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? t('actions.submitting') : t('common.submitRequest')}
            </button>
          </div>
        </form>
      </div>
    </div>   
  );
};

export default NewRequest;